import { folderLabel, isBriefFolder } from "@/domains/documents/utils/briefFolder";
import React from "react";
import type { TreeFolder } from "../../../types";
import { FolderItem } from "../../molecules/FolderTree/FolderItem";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { isSystemFolder } from "../../../services/folderService";

interface FolderTreeNodeProps {
  /** Folder data */
  folder: TreeFolder;
  /** Nesting level for indentation */
  level: number;
  /** Whether folder is expanded */
  isExpanded: boolean;
  /** Whether folder is selected */
  isSelected: boolean;
  /** Whether folder is being edited */
  isEditing: boolean;
  /** Current edit name value */
  editName: string;
  /** Whether folder is being dragged over */
  isDragOver: boolean;
  /** Whether folder is a valid destination for the active move. */
  isDropAvailable?: boolean;
  /** Native folder move source state. */
  isDraggable?: boolean;
  isDragging?: boolean;
  /** Called when folder is selected */
  onSelect: (folderId: string) => void;
  /** Called when expand/collapse is toggled */
  onToggleExpand: (folderId: string) => void;
  /** Called when name edit changes */
  onNameChange: (value: string) => void;
  /** Called when name edit is confirmed */
  onNameConfirm: (value: string) => void;
  /** Called when name edit is cancelled */
  onNameCancel: () => void;
  /** Called when create subfolder is selected */
  onCreateSubfolder: (folderId: string) => void;
  /** Called when rename is selected */
  onRename: (folder: TreeFolder) => void;
  /** Called when delete is selected */
  onDelete: (folderId: string) => void;
  /** Called when export is selected */
  onExport: (folderId: string) => void;
  /** Called when "Organize with AI" is selected (WHA-3654) */
  onOrganize?: (folderId: string) => void;
  /** Called when share is selected */
  onShare: (folderId: string) => void;
  /** Drag event handlers */
  onDragEnter: (folderId: string, hasChildren: boolean) => (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (folderId: string) => (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Recursive rendering function for children */
  renderChildren?: () => React.ReactNode;
}

/**
 * FolderTreeNode - Organism component
 *
 * Recursive folder rendering using atoms, molecules, and hooks.
 * Renders a single folder using FolderItem molecule and recursively renders children.
 * Handles drag/drop, expansion, selection, and inline editing.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderTreeNode
 *   folder={folder}
 *   level={0}
 *   isExpanded={isExpanded(folder.id)}
 *   isSelected={isSelected(folder.id)}
 *   isEditing={isEditing(folder.id)}
 *   editName={editName}
 *   isDragOver={isDraggedOver(folder.id)}
 *   onSelect={selectFolder}
 *   onToggleExpand={toggleExpanded}
 *   {...otherHandlers}
 * />
 * ```
 */
export const FolderTreeNode = React.memo(function FolderTreeNode({
  folder,
  level,
  isExpanded,
  isSelected,
  isEditing,
  editName,
  isDragOver,
  isDropAvailable = false,
  isDraggable = false,
  isDragging = false,
  onSelect,
  onToggleExpand,
  onNameChange,
  onNameConfirm,
  onNameCancel,
  onCreateSubfolder,
  onRename,
  onDelete,
  onExport,
  onOrganize,
  onShare,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDragOver,
  onDragStart,
  onDragEnd,
  renderChildren,
}: FolderTreeNodeProps) {
  const { t } = useLanguage();
  const hasChildren = folder.children.length > 0;
  // The Brief folder takes subfolders, but is never renamed, deleted or organised.
  const fixed = isSystemFolder(folder) || isBriefFolder(folder);

  return (
    <div
      onDragOver={onDragOver}
      onDragEnter={onDragEnter(folder.id, hasChildren)}
      onDragLeave={onDragLeave}
      onDrop={onDrop(folder.id)}
    >
      <FolderItem
        id={folder.id}
        name={isEditing ? editName : folderLabel(folder)}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        documentCount={folder.documentCount}
        isEditing={isEditing}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDropAvailable={isDropAvailable}
        isDraggable={isDraggable && !isEditing}
        isDragging={isDragging}
        level={level}
        isContainer={folder.isContainer}
        variant={folder.variant}
        onSelect={() => onSelect(folder.id)}
        onToggleExpand={() => onToggleExpand(folder.id)}
        onNameChange={onNameChange}
        onNameConfirm={onNameConfirm}
        onNameCancel={onNameCancel}
        // WHA-3270: the system Issue Attachments folder is read-only.
        // Pass undefined so FolderActionMenu hides the create / rename /
        // delete entries entirely (Export + Share stay available).
        onCreateSubfolder={isSystemFolder(folder) ? undefined : () => onCreateSubfolder(folder.id)}
        onRename={fixed ? undefined : () => onRename(folder)}
        onDelete={fixed ? undefined : () => onDelete(folder.id)}
        onExport={() => onExport(folder.id)}
        onOrganize={onOrganize && !fixed ? () => onOrganize(folder.id) : undefined}
        onShare={() => onShare(folder.id)}
        deleteLabel={t.documents.folder.deleteFolder}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      />
      {isExpanded && renderChildren && renderChildren()}
    </div>
  );
});
